import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { FeedBoard, FeedLeaderboardRow, FeedLeaderboardsResponse } from "@gooner/shared";
import { track } from "../../../analytics";
import { api } from "../../../lib/api";
import { Avatar } from "../../ui/Avatar";
import { CrownMark } from "../../ui/Crown";
import { FapsAmount } from "../../ui/FapsAmount";
import { GoldName } from "../../ui/Gold";
import { Tile } from "../../ui/Tile";
import { cx } from "../../ui/primitives";

/**
 * Today's three boards — minutes gooned, Faps earned, crowns given — top five each,
 * with an arrow against yesterday's rank. Public profiles only (the server's SQL is the
 * gate; nothing here hides anyone). Names link to the profile. Resets at midnight UTC,
 * and says so.
 */
const BOARDS: ReadonlyArray<{ key: FeedBoard; label: string }> = [
  { key: "gooning", label: "Goon time today" },
  { key: "faps", label: "Faps earned today" },
  { key: "crowns", label: "Crowns given today" },
];

export function Leaderboards() {
  const [boards, setBoards] = useState<FeedLeaderboardsResponse | null>(null);
  useEffect(() => {
    let cancelled = false;
    api
      .feedLeaderboards()
      .then((b) => !cancelled && setBoards(b))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);
  return (
    <div className="flex flex-col gap-3">
      {BOARDS.map((b) => (
        <Tile key={b.key} label={b.label}>
          <ol className="mt-2 space-y-1.5">
            {boards === null ? (
              <PlaceholderRows />
            ) : boards[b.key].length === 0 ? (
              <li className="text-xs text-zinc-500">Nobody yet today.</li>
            ) : (
              boards[b.key].map((r) => <Row key={r.username} board={b.key} row={r} />)
            )}
          </ol>
        </Tile>
      ))}
      <p className="px-1 text-[11px] text-zinc-600">Today · resets at midnight UTC · public profiles only</p>
    </div>
  );
}

/**
 * A board's loading state, which depends on where the boards sit.
 *
 * Beside the feed (`lg` and up, the 300px sidebar): a full board's worth of rows, each the
 * height of a real row (the h-7 avatar sets it), so a tile opens at its loaded size
 * instead of growing from one "Loading…" line to five rows and shoving the tiles under it.
 *
 * Below the feed (phones and tablets, under `lg`): the one "Loading…" line, as before.
 * There the boards sit under the posts, and the posts arrive and push them off-screen
 * anyway; a taller placeholder only made more of the page move when they did (phone
 * /feed got worse, 0.43 to 0.54). The rows are `hidden` there and the line is a plain
 * line; at `lg` the line turns screen-reader-only and the rows show.
 *
 * The line comes first with `mb-0`: below `lg` it is the only box in the list, so it must
 * not keep the `space-y` gap it gets for having (hidden) siblings after it.
 */
const PLACEHOLDER_ROWS = 5;
function PlaceholderRows() {
  return (
    <>
      <li className="mb-0 text-xs text-zinc-500 lg:sr-only">Loading…</li>
      {Array.from({ length: PLACEHOLDER_ROWS }).map((_, i) => (
        <li key={i} aria-hidden className="hidden h-7 items-center gap-2 lg:flex">
          <span className="h-3 w-11 rounded bg-white/5" />
          <span className="h-7 w-7 shrink-0 rounded-full bg-white/5" />
          <span className="h-3 flex-1 rounded bg-white/5" />
        </li>
      ))}
    </>
  );
}

function Row({ board, row }: { board: FeedBoard; row: FeedLeaderboardRow }) {
  return (
    <li className="flex items-center gap-2 text-sm">
      <span className="w-4 text-right text-xs tabular-nums text-zinc-500">{row.rank}</span>
      <Delta delta={row.delta} />
      <Avatar name={row.username} src={row.avatarUrl} size="sm" />
      <Link
        to={`/u/${encodeURIComponent(row.username)}`}
        onClick={() => track("feed_leaderboard_name_clicked", { board })}
        className={cx("min-w-0 flex-1 truncate text-sm font-medium hover:underline", row.gold ? "text-[var(--color-accent-soft)]" : "text-zinc-100")}
      >
        <GoldName name={row.username} gold={row.gold} />
      </Link>
      <span className="shrink-0 text-xs tabular-nums text-zinc-300">
        {board === "gooning" ? (
          minutesLabel(row.value)
        ) : board === "faps" ? (
          <FapsAmount n={row.value} size={11} />
        ) : (
          <span className="inline-flex items-center gap-1">
            <CrownMark size={12} /> {row.value}
          </span>
        )}
      </span>
    </li>
  );
}

/** ▲ climbed, ▼ fell, – held, "new" was not on yesterday's board. */
function Delta({ delta }: { delta: number | null }) {
  if (delta === null) return <span className="w-7 text-[10px] font-medium uppercase text-amber-300" title="New on the board">new</span>;
  if (delta === 0) return <span className="w-7 text-xs text-zinc-600" title="Same as yesterday">–</span>;
  const up = delta > 0;
  return (
    <span
      className={cx("w-7 text-xs tabular-nums", up ? "text-emerald-400" : "text-red-400")}
      title={`${up ? "Up" : "Down"} ${Math.abs(delta)} since yesterday`}
    >
      {up ? "▲" : "▼"}{Math.abs(delta)}
    </span>
  );
}

function minutesLabel(min: number): string {
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

/** Exported for tools/checks/replay-mask.mts, which renders a row with a sentinel
 *  username: the name is masked through GoldName and Avatar, the /u/ href by the global
 *  attribute rewrite. Nothing here needed a marker of its own. */
export { Row as LeaderboardRow };
